/* What a contact is worth — the one implementation, shared by the Suite's
   Contacts list and drawer and by tools/crm/dossier.mjs.

   It used to live in tools/crm/lib.mjs, where the browser could not reach it,
   so the CRM screens and the dossier answered "what is Ben worth?" with
   different arithmetic. It moved here (CRM v3, slice 3) and tools/crm/lib.mjs
   re-exports it, so the two surfaces can only ever agree.

   Money comes from src/apps/jobs/lib/revenue.js and nowhere else: a change
   order is part of its parent project (Ivin, 2026-08-25), so a rolled-up CO
   child job is skipped here and its money reaches the contact through the
   parent's `jobRevenueParts().total`.

   No React, no Firestore, no DOM — plain node can import it, which is what
   keeps the dossier honest. */

import { jobRevenueParts, isRolledUpChangeOrderJob } from "../apps/jobs/lib/revenue.js";

/** `$1,234`. Whole dollars — the CRM never shows cents on a summary line.
 *  Returns "" for null/undefined/non-numeric so a missing total prints blank
 *  rather than "$0" (which would read as a free job). */
export function formatMoney(n) {
  if (n === null || n === undefined || n === "") return "";
  const v = typeof n === "number" ? n : Number(String(n).replace(/[$,]/g, ""));
  if (!Number.isFinite(v)) return "";
  const sign = v < 0 ? "-" : "";
  return `${sign}$${Math.round(Math.abs(v)).toLocaleString("en-US")}`;
}

const asArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const idKey = (v) => String(v ?? "").trim().toLowerCase();

/** Normalize any CRM date value to YYYY-MM-DD. Copy of dateOnly() in
 *  tools/crm/lib.mjs — kept here so this module has no tools/ dependency. */
export function dateOnly(v) {
  const s = String(v ?? "").trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : "";
}

/** Latest date a job covers: a session date if there are sessions, else the
 *  root captureDate, else when it was quoted / created. */
export function jobDate(j) {
  const ds = [dateOnly(j?.captureDate), ...asArray(j?.sessions).map((s) => dateOnly(s?.date))]
    .filter(Boolean).sort();
  return ds[ds.length - 1] || dateOnly(j?.quoteDate) || dateOnly(j?.createdAt) || "";
}

/**
 * One project's revenue, through revenue.js.
 *
 * The one thing this adds is the legacy `total` fallback the dossier has
 * always applied: jobs migrated before `grossRevenue` existed carry only
 * `total`, and revenue.js's own fallback (the linked quote) cannot help when
 * that quote was never migrated either. An explicit `grossRevenue: 0` stays 0.
 *
 * The fallback replaces the PARENT'S OWN BASE PART, not the whole total. Tested
 * against the total it silently switched itself off the moment the job had a
 * change order — a legacy `{ total: 1200 }` parent with a $200 change order
 * summed to $200, not $1,400, because the total was no longer zero and the
 * parent's own $1,200 was never counted at all.
 */
export function jobAmount(job, quotes = [], jobs = []) {
  const parts = jobRevenueParts(job, quotes, jobs);
  let base = parts.base.amount;
  if (base === 0 && job?.grossRevenue == null) {
    const legacy = Number(job?.total);
    if (Number.isFinite(legacy) && legacy) base = legacy;
  }
  return parts.changeOrders.reduce((sum, co) => sum + co.amount, base);
}

const emptyBuckets = () => ({
  direct: { count: 0, amount: 0, jobs: [] },
  viaCompany: { count: 0, amount: 0 },
  referred: { count: 0, amount: 0 },
});

/** Every id a record can be pointed at by: the live id, the Firestore path id,
 *  and the pre-migration entity id. */
const allIdsOf = (r) => [r?.id, r?._docId, r?.old_entity_id].filter(Boolean).map(idKey);

/** Every id form → the client it stands for. Built once and reused, because
 *  the batch index asks the same question for 3,400 contacts in a row. */
const indexClientIds = (clients = []) => {
  const byId = new Map();
  for (const cl of clients || []) for (const k of allIdsOf(cl)) byId.set(k, cl);
  return byId;
};

/** The employer ids for one contact, against a prebuilt client lookup. */
function clientIdsWith(contact, clientsById) {
  const out = new Set();
  const links = [contact?.clientId, ...asArray(contact?.linkedClients).map((l) => l?.clientId)].filter(Boolean);
  for (const id of links) {
    out.add(idKey(id));
    const cl = clientsById.get(idKey(id));
    if (cl) allIdsOf(cl).forEach((v) => out.add(v));
  }
  return [...out];
}

/** Default employer ids for a contact when the caller does not supply them:
 *  the clients they are linked to, each expanded to every id form. Mirrors
 *  clientIdsFor() in tools/crm/dossier.mjs. */
export function clientIdsForContact(contact, clients = []) {
  return clientIdsWith(contact, indexClientIds(clients));
}

/* Bucket precedence for one (contact, job) pair. A job lands in exactly one
   bucket, so the three totals can be added up. */
const DIRECT = 3, VIA = 2, REFERRED = 1;

/**
 * The value every contact is attached to, in one pass.
 *
 * The Contacts list needs this for 3,400 people at once, and calling
 * contactValueRollup() per row would re-scan every job and every contact 3,400
 * times. This walks the jobs once and attributes each one to whoever it
 * belongs to, so the list costs one pass, not n.
 *
 * Buckets, exclusive in this order:
 *   direct      the job names this contact (`linkedContacts[].contactId`, or a
 *               legacy `linkedEntities[].entityId`)
 *   viaCompany  the job belongs to one of this contact's clients but does not
 *               name them — the work their employer brought in
 *   referred    the job belongs to someone whose `referredBy` points at this
 *               contact, by id or by name
 *
 * @returns Map keyed by idKey(contact.id) → { direct, viaCompany, referred }
 */
export function contactValueIndex({ contacts = [], jobs = [], clients = [], quotes = [], clientIdsFor } = {}) {
  const out = new Map();
  const bucketsFor = (key) => {
    let b = out.get(key);
    if (!b) { b = emptyBuckets(); out.set(key, b); }
    return b;
  };

  /* Every id form → the contact it stands for. */
  const contactByAnyId = new Map();
  const contactByName = new Map();
  for (const c of contacts || []) {
    if (!c) continue;
    const key = idKey(c.id);
    bucketsFor(key);
    for (const k of allIdsOf(c)) contactByAnyId.set(k, key);
    const n = idKey(c.name);
    if (n && !contactByName.has(n)) contactByName.set(n, key);
  }

  /* Employer id → the contacts who work there. The client lookup is built ONCE
     here, not rebuilt per contact: at 3,400 contacts that inner rebuild was the
     whole cost of the pass. */
  const clientsById = indexClientIds(clients);
  const contactsByClientId = new Map();
  for (const c of contacts || []) {
    if (!c) continue;
    const key = idKey(c.id);
    const ids = clientIdsFor ? clientIdsFor(c) : clientIdsWith(c, clientsById);
    for (const raw of ids || []) {
      const k = idKey(raw);
      if (!k) continue;
      if (!contactsByClientId.has(k)) contactsByClientId.set(k, new Set());
      contactsByClientId.get(k).add(key);
    }
  }

  /* Who sent us whom. `referredBy` is free text in the CRM — an id on newer
     records, a typed name on older ones — so both are accepted, and a record
     that says it referred itself is ignored. */
  const referrerOfContactId = new Map();
  const referrerOfClientId = new Map();
  const referrerKey = (record) => {
    const v = idKey(record?.referredBy);
    if (!v) return "";
    return contactByAnyId.get(v) || contactByName.get(v) || "";
  };
  const addReferral = (map, record, referrer) => {
    for (const k of allIdsOf(record)) {
      if (!map.has(k)) map.set(k, new Set());
      map.get(k).add(referrer);
    }
  };
  for (const c of contacts || []) {
    const referrer = referrerKey(c);
    if (referrer && referrer !== idKey(c.id)) addReferral(referrerOfContactId, c, referrer);
  }
  for (const cl of clients || []) {
    const referrer = referrerKey(cl);
    if (referrer) addReferral(referrerOfClientId, cl, referrer);
  }

  const liveJobs = (jobs || []).filter((j) => j && !j.deletedAt);

  for (const j of liveJobs) {
    // A change order's money is reported on its parent project, so the child
    // row is skipped outright rather than counted twice.
    if (isRolledUpChangeOrderJob(j, liveJobs)) continue;
    const amount = jobAmount(j, quotes, liveJobs);

    const linkIds = [
      ...asArray(j.linkedContacts).map((l) => idKey(l?.contactId)),
      ...asArray(j.linkedEntities).map((l) => idKey(l?.entityId)),
    ].filter(Boolean);
    const ownerIds = [
      idKey(j.clientId),
      ...asArray(j.linkedEntities).map((l) => idKey(l?.entityId)),
    ].filter(Boolean);

    const rank = new Map();
    const claim = (key, r) => { if (key && (rank.get(key) || 0) < r) rank.set(key, r); };

    for (const id of linkIds) claim(contactByAnyId.get(id), DIRECT);
    for (const id of ownerIds) for (const key of contactsByClientId.get(id) || []) claim(key, VIA);
    for (const id of linkIds) for (const key of referrerOfContactId.get(id) || []) claim(key, REFERRED);
    for (const id of ownerIds) for (const key of referrerOfClientId.get(id) || []) claim(key, REFERRED);

    for (const [key, r] of rank) {
      const b = bucketsFor(key);
      if (r === DIRECT) {
        b.direct.count++;
        b.direct.amount += amount;
        b.direct.jobs.push({ jobId: j.jobId || j.id, date: jobDate(j), amount });
      } else if (r === VIA) {
        b.viaCompany.count++;
        b.viaCompany.amount += amount;
      } else {
        b.referred.count++;
        b.referred.amount += amount;
      }
    }
  }

  for (const b of out.values()) {
    b.direct.jobs.sort((a, z) =>
      (z.date || "").localeCompare(a.date || "") || String(z.jobId).localeCompare(String(a.jobId)));
  }
  return out;
}

/**
 * The same answer for one person. Kept for tools/crm (the dossier resolves a
 * handful of contacts, not the whole book) and for anything that has an
 * explicit employer-id list to pass in.
 *
 * @param contact              the contact record
 * @param jobs                 every job (deleted ones are skipped)
 * @param clientIdsForContact  ids of this contact's clients, legacy
 *                             `old_entity_id`s included
 * @param contacts             every contact (to find who they referred)
 * @param clients              every client (same)
 * @param quotes               every quote — revenue.js needs them to value a
 *                             legacy job and an unbooked change order
 */
export function contactValueRollup(contact, jobs = [], clientIdsForContact = [], contacts = [], clients = [], quotes = []) {
  const me = idKey(contact?.id);
  const known = (contacts || []).some((c) => idKey(c?.id) === me);
  const index = contactValueIndex({
    contacts: known ? contacts : [contact, ...(contacts || [])],
    jobs,
    clients,
    quotes,
    // Only this contact's buckets are read, so nobody else needs employers.
    clientIdsFor: (c) => (idKey(c?.id) === me ? clientIdsForContact : []),
  });
  return index.get(me) || emptyBuckets();
}
